import React, { useMemo, useState } from 'react';

import Button from 'components/common/Button';
import Notice from 'components/common/Notice/Notice';
import {
  PLAN_CROP_MISMATCH_ACTUAL_MESSAGE,
  PLAN_CROP_MISMATCH_MESSAGE,
  PLAN_CROP_MISMATCH_SEVERAL_HINT,
} from 'constants/messageConstants';
import {
  PLAN_CROP_MISMATCH_KEEP_LABEL,
  PLAN_CROP_MISMATCH_USE_PLAN_CROP,
  PLAN_CROP_MISMATCH_USE_SEED_CROP,
} from 'constants/uiConstants';
import type { MergedCrop } from 'interfaces/IGlobalCatalogue';
import type { ICrop, IInput } from 'interfaces/IInput';
import type { IInputTask } from 'interfaces/IInputTask';

import {
  findPlanCropMismatch,
  seedWithPlanCrop,
} from './Tasks/planCropDefaults';

/** "Wheat (Scepter)" or "Wheat". */
const cropLabel = (crop: ICrop) =>
  crop.variety?.varietyName
    ? `${crop.cropName} (${crop.variety.varietyName})`
    : crop.cropName;

interface PlanCropMismatchNoticeProps {
  draft: IInput;
  /** The plan's crop in the catalogue, for the variety's details. */
  cropOption: MergedCrop | null;
  onSetPlanCrop: (crop: ICrop) => void;
  onUpdateTask: (task: IInputTask) => void;
}

/**
 * Alerts when the plan's crop and its crop establishment seed differ
 * (OP-2536). The plan can always take the crop establishment's crop in one
 * click; the task only takes the plan's when it's the plan's one Plan crop
 * establishment task sowing one seed — an Actual record is never changed.
 * Keep different hides it until either side changes again.
 */
const PlanCropMismatchNotice: React.FC<PlanCropMismatchNoticeProps> = ({
  draft,
  cropOption,
  onSetPlanCrop,
  onUpdateTask,
}) => {
  const mismatch = useMemo(() => findPlanCropMismatch(draft), [draft]);
  const [keptKeys, setKeptKeys] = useState<string[]>([]);
  if (!mismatch) return null;

  const planCrop = cropLabel(draft.crop);
  const seedCrop = cropLabel(mismatch.seedCrop);
  const key = `${draft.id}|${planCrop}|${seedCrop}`;
  if (keptKeys.includes(key)) return null;

  const { update } = mismatch;
  const message = mismatch.isActual
    ? PLAN_CROP_MISMATCH_ACTUAL_MESSAGE(seedCrop, planCrop)
    : PLAN_CROP_MISMATCH_MESSAGE(seedCrop, planCrop);

  return (
    <Notice
      size="md"
      className="mt-3 w-full max-w-2xl"
      actions={
        <>
          {mismatch.canUseSeedCrop && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="px-2"
              onClick={() => onSetPlanCrop(mismatch.seedCrop)}
            >
              {PLAN_CROP_MISMATCH_USE_SEED_CROP(seedCrop)}
            </Button>
          )}
          {update && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="px-2"
              onClick={() =>
                onUpdateTask({
                  ...update.task,
                  steps: update.task.steps.map((step) =>
                    step.stepId === update.seed.stepId
                      ? seedWithPlanCrop(update.seed, draft.crop, cropOption)
                      : step
                  ),
                })
              }
            >
              {PLAN_CROP_MISMATCH_USE_PLAN_CROP(planCrop)}
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="px-2"
            onClick={() => setKeptKeys((previous) => [...previous, key])}
          >
            {PLAN_CROP_MISMATCH_KEEP_LABEL}
          </Button>
        </>
      }
    >
      {message}
      {!update && !mismatch.isActual && <> {PLAN_CROP_MISMATCH_SEVERAL_HINT}</>}
    </Notice>
  );
};

export default PlanCropMismatchNotice;
